import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Box, ClickAwayListener, MenuItem, MenuList, Paper, Popper, Tab, Tabs } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { researcherFontFamily } from '../researcherPresentation';

export type StudyNavigationValue = 'participants' | 'assessments' | 'extraction' | 'visualization' | 'adherence' | 'study' | 'chat';
type MenuOption = { label: string; icon?: React.ReactNode; onSelect: () => void; selected?: boolean; spotlightId?: string };
export type StudyNavigationItem = {
  value: StudyNavigationValue;
  label: React.ReactNode;
  icon?: React.ReactNode;
  spotlightId?: string;
} & ({ options: MenuOption[]; onSelect?: never } | { onSelect: () => void; options?: never });

export default function StudyNavigation({ value, items }: { value: StudyNavigationValue; items: StudyNavigationItem[] }) {
  const id = useId();
  const [menu, setMenu] = useState<{ value: StudyNavigationValue; anchor: HTMLElement; focus: boolean } | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuId = `${id}-menu`;
  const options = items.find(item => item.value === menu?.value)?.options || [];
  const cancelClose = () => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const closeMenu = (restoreFocus = false) => {
    cancelClose();
    if (restoreFocus) menu?.anchor.focus({ preventScroll: true });
    setMenu(null);
  };
  const openMenu = (item: StudyNavigationItem, anchor: HTMLElement, focus: boolean) => {
    cancelClose();
    if (!item.options) { setMenu(null); return; }
    setMenu(current => current?.value === item.value && current.focus === focus ? current : { value: item.value, anchor, focus });
  };
  const scheduleClose = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return;
    cancelClose();
    // Allow the pointer to cross the small gap between a tab and its menu.
    closeTimer.current = setTimeout(() => setMenu(null), 180);
  };
  useEffect(() => () => { if (closeTimer.current !== null) clearTimeout(closeTimer.current); }, []);
  const focusMenu = useCallback((list: HTMLUListElement | null) => {
    if (menu?.focus) list?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
  }, [menu]);

  return <ClickAwayListener onClickAway={() => closeMenu()}>
    <Box component="nav" aria-label="Study navigation" sx={{ borderBottom: '1px solid', borderColor: '#eeeef1' }}>
      <Tabs value={value} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Study sections"
        TabIndicatorProps={{ sx: { height: 3, borderRadius: 999 } }}
        sx={{ minHeight: 58, px: { xs: 0.5, sm: 2 },
          // Center when there is room; keep every tab reachable by scrolling on narrow screens.
          '& .MuiTabs-flexContainer': { width: 'max-content', minWidth: '100%', justifyContent: 'center', gap: 0.5 },
          '& .MuiTab-root': { fontFamily: researcherFontFamily, fontSize: 14, minHeight: 58, minWidth: 0, px: { xs: 1.5, sm: 2 },
            textTransform: 'none', fontWeight: 600, borderRadius: 1.5, color: 'text.secondary' },
          '& .MuiTab-root.Mui-selected': { color: 'primary.main' },
        }}>
        {items.map(item => {
          const open = menu?.value === item.value;
          return <Tab key={item.value} value={item.value} id={`${id}-${item.value}`} data-spotlight-id={item.spotlightId}
            aria-haspopup={item.options ? 'menu' : undefined} aria-expanded={item.options ? open : undefined} aria-controls={open ? menuId : undefined}
            label={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
              {item.icon && <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', '& svg': { fontSize: 18 } }}>{item.icon}</Box>}
              {item.label}
              {item.options && <ExpandMore fontSize="small" sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 140ms ease', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }} />}</Box>}
            sx={{ bgcolor: open ? '#f8f1f1' : undefined }}
            onPointerEnter={event => { if (event.pointerType !== 'touch') openMenu(item, event.currentTarget, false); }}
            onPointerLeave={scheduleClose}
            onFocus={() => { if (menu && !open) closeMenu(); }}
            onClick={event => {
              if (item.options) {
                // Pointer clicks must not focus the portaled menu and scroll the page.
                openMenu(item, event.currentTarget, event.detail === 0);
              } else { closeMenu(); item.onSelect(); }
            }}
            onKeyDown={event => {
              if (item.options && ['ArrowDown', 'Enter', ' '].includes(event.key)) {
                event.preventDefault(); openMenu(item, event.currentTarget, true);
              } else if (event.key === 'Escape') { event.preventDefault(); closeMenu(); }
            }} />;
        })}
      </Tabs>
      <Popper open={Boolean(menu)} anchorEl={menu?.anchor} placement="bottom-start" sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }, { name: 'preventOverflow', options: { padding: 12 } }]}>
        <Paper elevation={0} onPointerEnter={cancelClose} onPointerLeave={scheduleClose}
          sx={{ minWidth: 220, maxWidth: 'calc(100vw - 24px)', p: 0.5, borderRadius: 2, border: '1px solid #e7e8ec', boxShadow: '0 8px 28px rgba(24,32,48,0.12)' }}>
          <MenuList ref={focusMenu} id={menuId} aria-labelledby={menu ? `${id}-${menu.value}` : undefined}
            onKeyDown={event => {
              if (event.key === 'Escape' || event.key === 'Tab') { event.preventDefault(); closeMenu(true); }
            }}
            onBlur={event => {
              if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node) && event.relatedTarget !== menu?.anchor) closeMenu();
            }}>
            {options.map(option => <MenuItem key={option.label} selected={option.selected} data-spotlight-id={option.spotlightId}
              onClick={() => { closeMenu(true); option.onSelect(); }}
              sx={{ borderRadius: 1.5, minHeight: 40, fontSize: 14, fontFamily: researcherFontFamily,
                '&.Mui-selected': { bgcolor: '#f6eaea', color: 'primary.main' }, '&:hover': { bgcolor: '#f8f1f1' } }}>
              {option.icon && <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', mr: 1, '& svg': { fontSize: 18 } }}>{option.icon}</Box>}
              {option.label}
            </MenuItem>)}
          </MenuList>
        </Paper>
      </Popper>
    </Box>
  </ClickAwayListener>;
}
